/**
 * Background checks on a KYC case (admin doc 06 §3.2): order one from an approved vendor, then
 * record what the vendor said. Split from `KycQueue.tsx` so each stays readable.
 *
 * **The vendor list is the server's.** It lives in the `kyc.approved_bgv_vendors` setting, and an
 * empty list is the correct state before a data-processing agreement is signed. The console does
 * not copy it; a vendor not on it is refused with `vendor_not_approved`, shown verbatim.
 *
 * **A result is written once.** Only a pending check offers "Record result", and the server
 * refuses to overwrite one that is already decided. A discrepancy is not a failure: it is a
 * question to ask the pandit, and it never blocks anything on its own.
 */
import { useState } from 'react';
import { Note, StatusChip } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { ScopeAction, Section } from '../components/kit';
import { EmptyState, ErrorState } from '../components/feedback';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { istDay } from './PricingRules';
import {
  useOrderBackgroundCheck,
  useRecordBackgroundCheck,
  type BackgroundCheckRow,
} from '../lib/api/hooks/kyc';

export const CHECK_SCOPES = ['criminal_record', 'address_verification', 'court_record', 'composite'] as const;
export const CHECK_RESULTS = ['clear', 'discrepancy', 'failed'] as const;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const tone = (result: string) =>
  result === 'clear' ? 'success' : result === 'failed' ? 'error' : result === 'pending' ? 'info' : 'warn';

function RecordResult({ check, onDone }: { check: BackgroundCheckRow; onDone: () => void }) {
  const { scope, toast } = useConsole();
  const record = useRecordBackgroundCheck();
  const [result, setResult] = useState<string>('clear');
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const dateOk = validUntil === '' || ISO_DATE.test(validUntil);

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)', marginTop: 'var(--sp-sm)' }}>
      <Select label="Result" options={CHECK_RESULTS} value={result} onChange={setResult} />
      <Input
        label="Valid until"
        value={validUntil}
        onChange={setValidUntil}
        placeholder="2027-10-22"
        helper="YYYY-MM-DD · when the badge this check backs must be re-checked"
        error={dateOk ? undefined : 'A date as YYYY-MM-DD'}
      />
      <Input label="Notes" value={notes} onChange={setNotes} helper="What the vendor reported" />
      {record.isError && (
        <ErrorState inline title="The server refused this result" cause={errorMessage(record.error)} />
      )}
      <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
        <ScopeAction
          scope={scope}
          need="supply_kyc"
          size="sm"
          disabled={!dateOk || record.isPending}
          onClick={() =>
            record.mutate(
              { checkId: check.id, result, valid_until: validUntil, notes: notes.trim() },
              {
                onSuccess: () => {
                  toast({ tone: result === 'failed' ? 'info' : 'success', message: `Recorded ${result}` });
                  onDone();
                },
              },
            )
          }
        >
          Save result
        </ScopeAction>
        <ScopeAction scope={scope} need="supply_kyc" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </ScopeAction>
      </div>
    </div>
  );
}

function OrderCheck({ caseId, onDone }: { caseId: number; onDone: () => void }) {
  const { scope, toast } = useConsole();
  const order = useOrderBackgroundCheck();
  const [vendor, setVendor] = useState('');
  const [checkScope, setCheckScope] = useState<string>('composite');
  const [reference, setReference] = useState('');

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)', marginTop: 'var(--sp-md)' }}>
      <Input
        label="Vendor"
        value={vendor}
        onChange={setVendor}
        helper="Exactly as named in the approved-vendor setting"
      />
      <Select label="Scope" options={CHECK_SCOPES} value={checkScope} onChange={setCheckScope} />
      <Input label="Vendor reference" value={reference} onChange={setReference} helper="Optional" />
      {order.isError && (
        <ErrorState inline title="The server refused this order" cause={errorMessage(order.error)} />
      )}
      <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
        <ScopeAction
          scope={scope}
          need="supply_kyc"
          size="sm"
          disabled={vendor.trim() === '' || order.isPending}
          onClick={() =>
            order.mutate(
              {
                caseId,
                provider_name: vendor.trim(),
                check_scope: checkScope,
                provider_reference: reference.trim(),
              },
              {
                onSuccess: () => {
                  toast({ tone: 'success', message: 'Background check ordered' });
                  onDone();
                },
              },
            )
          }
        >
          Place order
        </ScopeAction>
        <ScopeAction scope={scope} need="supply_kyc" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </ScopeAction>
      </div>
    </div>
  );
}

export function BackgroundChecks({
  caseId,
  checks,
}: {
  caseId: number;
  checks: readonly BackgroundCheckRow[];
}) {
  const { scope } = useConsole();
  const [ordering, setOrdering] = useState(false);
  const [recording, setRecording] = useState<number | null>(null);

  return (
    <Section title="Background checks">
      <Note>
        A discrepancy never auto-blocks — a mismatched house number is something to ask about, not
        something to end an application over. Only `clear` issues the badge.
      </Note>
      {checks.length === 0 && <EmptyState icon="shield" title="No check ordered" inline />}
      {checks.map((b) => (
        <div
          key={b.id}
          style={{
            padding: 'var(--sp-sm) var(--sp-md)',
            border: '1px solid var(--c-border)',
            borderRadius: 'var(--r-tile)',
            marginTop: 'var(--sp-sm)',
          }}
        >
          <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500 }}>
                {b.provider_name} · {b.check_scope.replace(/_/g, ' ')}
              </div>
              <div style={{ color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}>
                {b.valid_until ? `valid to ${b.valid_until}` : `ordered ${istDay(b.requested_at)}`}
                {b.notes ? ` · ${b.notes}` : ''}
              </div>
            </div>
            <StatusChip tone={tone(b.result)} label={b.result} />
            {b.result === 'pending' && recording !== b.id && (
              <ScopeAction scope={scope} need="supply_kyc" size="sm" onClick={() => setRecording(b.id)}>
                Record result
              </ScopeAction>
            )}
          </div>
          {recording === b.id && <RecordResult check={b} onDone={() => setRecording(null)} />}
        </div>
      ))}
      {ordering ? (
        <OrderCheck caseId={caseId} onDone={() => setOrdering(false)} />
      ) : (
        <div style={{ marginTop: 'var(--sp-md)' }}>
          <ScopeAction scope={scope} need="supply_kyc" size="sm" onClick={() => setOrdering(true)}>
            Order check
          </ScopeAction>
        </div>
      )}
    </Section>
  );
}
